import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddContactModalComponent } from '@app/components/add-contact-modal/add-contact-modal.component';
import { ContactRequest, UserAccount } from '@app/models/account.model';
import { ContactService } from '@app/services/admin/contact.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef } from 'ag-grid-community';
import { finalize } from 'rxjs';

type Row = {
  id: string;
  name: string;
  email: string;
};

@Component({
  selector: 'app-contact-list',
  standalone: true,
  imports: [
    CommonModule,
    GridTableComponent,
    AddContactModalComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './contact-list.component.html',
  styleUrl: './contact-list.component.scss',
})
export class ContactListComponent implements OnInit {
  private readonly contactService = inject(ContactService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  columns: ColDef[] = [];
  rows: Row[] = [];
  showAddModal = signal(false);
  readonly isLoading = signal(true);

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
    this.load();
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'name',
        headerName: this.translate.instant('admin.contacts.table.name'),
      },
      {
        field: 'email',
        headerName: this.translate.instant('admin.contacts.table.email'),
      },
    ];
  }

  private load(): void {
    this.isLoading.set(true);
    this.contactService
      .getContacts()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const list: UserAccount[] = Array.isArray(res)
            ? res
            : res?.data ?? [];
          this.rows = list.map((user) => ({
            id: user.id,
            name: this.fullNameFromUser(user) || (user.email ?? '-'),
            email: user.email ?? '-',
          }));
        },
        error: () => {
          this.rows = [];
          this.toast.error(
            this.translate.instant('admin.toasts.tableLoadFailed')
          );
        },
      });
  }

  //TODO: remove it when api will be available
  private fullNameFromUser(user: UserAccount): string {
    return [
      user.personalData?.firstName,
      user.personalData?.midName,
      user.personalData?.lastName,
    ]
      .map((param) => (param ?? '').trim())
      .filter(Boolean)
      .join(' ');
  }

  openAddModal() {
    this.showAddModal.set(true);
  }
  closeAddModal() {
    this.showAddModal.set(false);
  }

  handleAdd(payload: ContactRequest): void {
    this.contactService.addContact(payload).subscribe({
      next: () => {
        const id = crypto.randomUUID();
        const displayName = [
          payload.firstName,
          payload.middleName,
          payload.lastName,
        ]
          .map((p) => (p ?? '').trim())
          .filter(Boolean)
          .join(' ');

        this.closeAddModal();
      },
      error: () => {
        this.toast.error(
          this.translate.instant('admin.toasts.contactAddFailed')
        );
      },
    });
  }
}
